import { useEffect, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { xml } from "@codemirror/lang-xml";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { rust } from "@codemirror/lang-rust";
import { php } from "@codemirror/lang-php";
import { java } from "@codemirror/lang-java";
import { go } from "@codemirror/lang-go";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { ruby } from "@codemirror/legacy-modes/mode/ruby";
import { swift } from "@codemirror/legacy-modes/mode/swift";
import { powerShell } from "@codemirror/legacy-modes/mode/powershell";
import { csharp } from "@codemirror/legacy-modes/mode/clike";
import { StreamLanguage } from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { githubLight } from "@uiw/codemirror-theme-github";
import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

export type SyntaxLang =
  | "json" | "html" | "xml" | "css" | "javascript" | "typescript"
  | "python" | "rust" | "php" | "java" | "go"
  | "shell" | "ruby" | "swift" | "powershell" | "csharp"
  | "plain";

export function detectLangFromContentType(contentType: string | null | undefined): SyntaxLang {
  const ct = (contentType ?? "").toLowerCase();
  if (!ct) return "plain";
  if (ct.includes("json")) return "json";
  if (ct.includes("html")) return "html";
  if (ct.includes("xml")) return "xml";
  if (ct.includes("css")) return "css";
  if (ct.includes("javascript") || ct.includes("ecmascript")) return "javascript";
  return "plain";
}

function extensionsFor(lang: SyntaxLang): Extension[] {
  const wrap = EditorView.lineWrapping;
  switch (lang) {
    case "json":       return [json(), wrap];
    case "html":       return [html(), wrap];
    case "xml":        return [xml(), wrap];
    case "css":        return [css(), wrap];
    case "javascript": return [javascript(), wrap];
    case "typescript": return [javascript({ typescript: true }), wrap];
    case "python":     return [python(), wrap];
    case "rust":       return [rust(), wrap];
    case "php":        return [php(), wrap];
    case "java":       return [java(), wrap];
    case "go":         return [go(), wrap];
    case "shell":      return [StreamLanguage.define(shell), wrap];
    case "ruby":       return [StreamLanguage.define(ruby), wrap];
    case "swift":      return [StreamLanguage.define(swift), wrap];
    case "powershell": return [StreamLanguage.define(powerShell), wrap];
    case "csharp":     return [StreamLanguage.define(csharp), wrap];
    default:           return [wrap];
  }
}

interface Props {
  value: string;
  lang: SyntaxLang;
  /** Optional max-height for the viewer (defaults to 100% of parent). */
  maxHeight?: string;
}

// Read-only CodeMirror with syntax highlighting + line numbers. Used for
// formatted response bodies. Theme follows the app's dark/light toggle.
export default function CodeViewer({ value, lang, maxHeight }: Props) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const extensions = useMemo(() => extensionsFor(lang), [lang]);
  return (
    <CodeMirror
      value={value}
      readOnly
      editable={false}
      theme={isDark ? oneDark : githubLight}
      extensions={extensions}
      height="100%"
      maxHeight={maxHeight}
      basicSetup={{
        lineNumbers: true,
        highlightActiveLine: false,
        bracketMatching: true,
        foldGutter: true,
        searchKeymap: false,
        autocompletion: false,
        indentOnInput: false,
        highlightActiveLineGutter: false,
      }}
      className="cm-host border border-border rounded"
    />
  );
}

// Pretty-print a JSON string if it parses; otherwise return the original
// untouched so the viewer still shows the raw bytes.
export function tryPrettyJson(s: string): string {
  try {
    const obj = JSON.parse(s);
    return JSON.stringify(obj, null, 2);
  } catch {
    return s;
  }
}
